/**
 * An invoice the commission engine cannot price on its date (piece 26;
 * decisions 1220, 1221). 🔒 The invoice still issues: the number is taken, the
 * receivable posts and the customer's copy is written, and the commission alone
 * waits, Unpriced, until a rate covering the deal type and the date is approved.
 *
 * This module holds what every reader of that state shares: the audit field the
 * issue writes it on, the reader of that field, and the warning's sentences, so
 * the invoice page, its to-do and its History cannot word it three ways.
 */
import { fill, t } from '../strings';
import type { CommissionGap } from './commission';
import { counted } from '../composites/countedUnit';

/** 🔒 The changed field the `commission.unpriced` audit row keeps the engine's refusal on (decision 1220). */
export const UNPRICED_COMMISSION_FIELD = 'commission.unpriced';

/** The route of Settings › CRM › Commission Rate Table, where the missing rate or band is added. */
export const COMMISSION_RATE_TABLE_ROUTE = '/settings/accounting/commission-rate-table';

/** What the issue kept when the engine refused: its reason, what was missing, and the to-dos it raised. */
export interface StoredUnpricedCommission {
  readonly reason: string;
  readonly gap: CommissionGap;
  readonly invoiceNumber: string | null;
  readonly dealId: string;
  /** The to-dos raised for this invoice, which close themselves when it is priced (decision 1221). */
  readonly todoIds: readonly string[];
}

/** A rate proposed for the deal type and waiting for its second person, which the warning names (decision 1221). */
export interface PendingCommissionRate {
  readonly dealType: string;
  readonly startDate: string;
}

/**
 * 🔒 The warning the invoice page draws while its commission is unpriced
 * (decision 1221): the sentence, the second sentence for this viewer, and the
 * link to the Commission Rate Table for someone who can add the rate there.
 */
export interface UnpricedCommissionView {
  /** The engine's own reason, as the Sales Commission Report prints it. */
  readonly reason: string;
  readonly message: string;
  readonly guidance: string;
  /** Set only for a viewer who can add the rate, and opens the add form set to the deal type and the invoice date. */
  readonly rateTableRoute: string | null;
}

/** The `commission.unpriced` row's kept refusal, or null for a row that keeps none. */
export function storedUnpricedOf(changedFields: unknown): StoredUnpricedCommission | null {
  if (!Array.isArray(changedFields)) return null;
  const entry = changedFields.find((f): f is { field: string; to: unknown } => typeof f === 'object' && f !== null && (f as { field?: unknown }).field === UNPRICED_COMMISSION_FIELD);
  const to = entry?.to as Partial<StoredUnpricedCommission> | undefined;
  if (!to || typeof to.reason !== 'string' || typeof to.dealId !== 'string' || typeof to.gap !== 'object' || to.gap === null) return null;
  return { reason: to.reason, gap: to.gap, invoiceNumber: to.invoiceNumber ?? null, dealId: to.dealId, todoIds: Array.isArray(to.todoIds) ? to.todoIds.filter((id): id is string => typeof id === 'string') : [] };
}

/** A calendar day as a person reads it: `2026-09-28` is 28 September 2026, never shifted by a zone. */
export function dayInWords(isoDay: string): string {
  const at = new Date(`${isoDay.slice(0, 10)}T00:00:00.000Z`);
  if (Number.isNaN(at.getTime())) return isoDay;
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(at);
}

function ageInWords(months: number): string {
  return counted(months, 'month');
}

/**
 * 🔒 The warning's first sentence (decision 1221): the storyboard's refusal
 * reworded as a warning, naming the deal type and the invoice date together, or
 * the account's age and the date for a gap in the bands. `subject` is *This
 * invoice* on the page and the invoice's number in a to-do.
 */
export function unpricedCommissionMessage(stored: Pick<StoredUnpricedCommission, 'gap' | 'reason'>, subject: string = t('commission.unpriced.thisInvoice')): string {
  const gap = stored.gap;
  if (gap.kind === 'Rate') return fill(t('commission.unpriced.rate'), { subject, dealType: gap.dealType, date: dayInWords(gap.saleDate) });
  if (gap.kind === 'Tier') return fill(t('commission.unpriced.tier'), { subject, age: ageInWords(gap.ageMonths), date: dayInWords(gap.saleDate) });
  return fill(t('commission.unpriced.other'), { subject, reason: stored.reason });
}

/**
 * 🔒 The Commission Rate Table's add form set to what is missing (decision
 * 1221): the deal type and the invoice date for a rate, the account's age and
 * the date for a band. The date can be moved earlier on the form.
 */
export function commissionRateTableRoute(gap: CommissionGap): string {
  if (gap.kind === 'Rate') return `${COMMISSION_RATE_TABLE_ROUTE}?dealType=${encodeURIComponent(gap.dealType)}&startDate=${gap.saleDate}`;
  if (gap.kind === 'Tier') return `${COMMISSION_RATE_TABLE_ROUTE}?tierFrom=${gap.ageMonths}&tierStart=${gap.saleDate}`;
  return COMMISSION_RATE_TABLE_ROUTE;
}

/**
 * 🔒 The whole warning for one viewer (decision 1221). A proposed rate waiting
 * for its approval is named; otherwise someone who can add the rate is told to
 * add it and offered the link, and anyone else is told the people who manage
 * commission rates already hold a to-do for it.
 */
export function unpricedCommissionView(stored: Pick<StoredUnpricedCommission, 'gap' | 'reason'>, facts: { pending: PendingCommissionRate | null; mayOpenRateTable: boolean }): UnpricedCommissionView {
  const gap = stored.gap;
  let guidance: string;
  if (gap.kind === 'Rate' && facts.pending) guidance = fill(t('commission.unpriced.pending'), { dealType: facts.pending.dealType, date: dayInWords(facts.pending.startDate) });
  else if (!facts.mayOpenRateTable) guidance = t('commission.unpriced.ask');
  else if (gap.kind === 'Rate') guidance = fill(t('commission.unpriced.addRate'), { dealType: gap.dealType });
  else if (gap.kind === 'Tier') guidance = fill(t('commission.unpriced.addTier'), { age: ageInWords(gap.ageMonths) });
  else guidance = t('commission.unpriced.fixOther');
  return { reason: stored.reason, message: unpricedCommissionMessage(stored), guidance, rateTableRoute: facts.mayOpenRateTable ? commissionRateTableRoute(gap) : null };
}

/** The to-do's title, naming the invoice (decision 1221). */
export function unpricedTodoTitle(invoiceNumber: string): string {
  return fill(t('commission.unpriced.todoTitle'), { invoice: invoiceNumber });
}

/** The to-do's body: the warning's sentence naming the invoice, and how it closes. */
export function unpricedTodoDescription(stored: Pick<StoredUnpricedCommission, 'gap' | 'reason'>, invoiceNumber: string): string {
  return `${unpricedCommissionMessage(stored, fill(t('commission.unpriced.invoiceSubject'), { invoice: invoiceNumber }))} ${t('commission.unpriced.todoCloses')}`;
}

/** The History line on the invoice and on its Deal, naming the invoice (decision 1221). */
export function unpricedHistoryLine(action: 'unpriced' | 'priced', invoiceNumber: string): string {
  return fill(t(action === 'unpriced' ? 'history.audit.commissionUnpriced' : 'history.audit.commissionPriced'), { invoice: invoiceNumber });
}
